
import React, { useState, useMemo } from 'react';
import '../styles/Credits.css';
import { creditsData, CreditYear, CreditProject } from './CreditsData';
import { motion, AnimatePresence } from 'framer-motion';
import { useSEO, seoConfigs } from '../hooks/useSEO';

const ExternalLinkIcon = () => (
  <span className="external-link-icon">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
    </svg>
  </span>
);

export const CreditsPage: React.FC = () => {
  useSEO(seoConfigs.credits);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({});

  // Initialize expanded state: all open by default or logic based on search
  // Original behavior: All collapsed initially? No, logic said "No initial collapsing active by default."

  const toggleYear = (year: string) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year] // If undefined, !undefined is true (expand). If explicit true, then false.
    }));
  };

  const handleExpandAll = () => {
    // To expand all, we can just clear the "collapsed" map if we treat undefined as expanded,
    // OR we set all known years to true.
    // Let's adopt: undefined = expanded (default). true = expanded. false = collapsed.
    // Wait, original logic: `toggle.classList.toggle('collapsed', isCollapsedInitially);`
    // Initial state function `setInitialCollapseState` was empty. So all open by default.
    // Collapsed class hides it.
    // My state: `expandedYears` -> if true (or undefined?), show. If false, hide.
    // Let's use: key exists and is FALSE -> collapsed. Otherwise expanded.
    setExpandedYears({});
  };

  const handleCollapseAll = () => {
    const allCollapsed: Record<string, boolean> = {};
    creditsData.forEach(y => allCollapsed[y.year] = false);
    setExpandedYears(allCollapsed);
  };

  // Filter Logic
  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return { data: creditsData, matchCount: 0, isFiltering: false };

    let count = 0;
    const filtered = creditsData.map(yearGroup => {
      const matchingProjects = yearGroup.projects.filter(p => {
        const searchString = `
                    ${p.title} 
                    ${p.director} 
                    ${p.details} 
                    ${p.role} 
                    ${p.note || ''} 
                    ${p.links?.map(l => l.text).join(' ') || ''} 
                    ${yearGroup.year}
                `.toLowerCase();
        const matches = searchString.includes(term);
        if (matches) count++;
        return matches;
      });

      // If the year section matches but no projects, should we show all projects?
      // Original logic: "sectionItselfMatches = yearText.includes(searchTerm)"
      // "sectionShouldBeVisible = !isSearchActive || sectionItselfMatches || sectionContainsMatchingItem"
      // If section matches, show section. But do we filter items inside?
      // Original: "if (itemMatchesSearch) ... else item.classList.add('hidden-by-search')"
      // So even if year matches, non-matching items are hidden.

      const yearMatches = yearGroup.year.toLowerCase().includes(term);

      if (yearMatches) {
        // If year matches, we include the year, but we still strictly filter projects?
        // Original Code: 
        // listItems.forEach... if itemMatchesSearch... element visible.
        // "sectionShouldBeVisible ... || sectionItselfMatches"
        // If sectionMatches, the SECTION is visible. But individual LIs might still be hidden if they don't match.
        // So we need to return matching projects OR all projects if we want that behavior?
        // Actually original JS hides non-matching LIs even if section matches.
        // So we safely just return matchingProjects. 
        // WAIT: If year matches, does it show ALL projects?
        // Logic: `item.classList.add('hidden-by-search')` happens unconditionally if item doesn't match.
        // So YES, even if year matches, only matching items show.
        // EXCEPTION: What if matches = 0 but year matches? Section is visible but empty?
        // Original: `sectionShouldBeVisible` is true. `content` block is... visible. `listItems` hidden.
        // So yes, empty expanded section.

        return {
          ...yearGroup,
          projects: matchingProjects,
          forceVisible: true // Indicator that year matched
        };
      }

      return {
        ...yearGroup,
        projects: matchingProjects,
        forceVisible: false
      };
    }).filter(group => group.projects.length > 0 || group.forceVisible);

    return { data: filtered, matchCount: count, isFiltering: true };
  }, [searchTerm]);

  // Derived state for expansion when filtering
  // Original: "if (isSearchActive && sectionContainsMatchingItem) ... content.classList.add('force-expand-search')"
  // React approach: If filtering, force expand sections with results.
  // We can compute if a section should be open during render.

  return (
    <div className="credits-page-wrapper">
      <div className="credits-container">
        <div className="sticky-header">
          <motion.div 
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-sky-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
              Credits & Portfolio
            </h1>
            <p className="text-gray-300 max-w-3xl mx-auto leading-relaxed">
              A journey started from 2009 through films, documentaries, and music projects. 
              Each entry represents a unique sonic experience crafted for storytelling.
            </p>
            <div className="mt-4 text-sm text-sky-200/70">
              <p>Click on any year to explore projects • Use search to filter by title, director, or role</p>
            </div>
          </motion.div>

          <div className="controls-container">
            <input
              type="search"
              className="credits-search-input"
              placeholder="Search credits (title, director, role, year)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="cinematic-button control-button" onClick={handleExpandAll}>Expand All</button>
            <button className="cinematic-button control-button" onClick={handleCollapseAll}>Collapse All</button>
          </div>

          <div className={`search-count-status ${filteredData.isFiltering && filteredData.matchCount > 0 ? 'visible' : ''}`} role="status">
            {filteredData.isFiltering && (
              filteredData.matchCount === 0
                ? '' // No results handled by main area
                : `${filteredData.matchCount} ${filteredData.matchCount === 1 ? 'match' : 'matches'} found.`
            )}
          </div>
        </div>

        <div className="scrollable-content">
          <div className="timeline-line"></div>
          <div className="credits-output">
            <AnimatePresence>
              {filteredData.data.map((yearGroup) => {
                const isExplicitlyCollapsed = expandedYears[yearGroup.year] === false;
                // If filtering and we have projects, force expand (ignore explicit collapse unless user re-collapsed? Original logic forces expand)
                // "content.classList.remove('collapsed')"
                const hasProjects = yearGroup.projects.length > 0;
                const isFiltering = filteredData.isFiltering;

                const isOpen = isFiltering && hasProjects ? true : !isExplicitlyCollapsed;

                return (
                  <motion.section
                    key={yearGroup.year}
                    className="credits-year-section"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2
                      className={`year-toggle ${!isOpen ? 'collapsed' : ''}`}
                      role="button"
                      aria-expanded={isOpen}
                      onClick={() => toggleYear(yearGroup.year)}
                    >
                      {yearGroup.year}
                      <span className="toggle-indicator">{isOpen ? '−' : '+'}</span>
                    </h2>

                    {/* Collapsible Content */}
                    <div className={`credits-list ${!isOpen ? 'collapsed' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}>
                      {yearGroup.projects.map((project, idx) => (
                        <li key={`${project.title}-${idx}`}>
                          <em className="title">{project.title}</em>{' '}
                          <span className="director">{project.director}</span>{' '}
                          <span className="details">{project.details}</span>
                          <span className="role">{project.role}</span>

                          {project.links && project.links.length > 0 && (
                            <div style={{ display: 'inline' }}>
                              {project.links.map((link, lIdx) => (
                                <a
                                  key={lIdx}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="credit-link"
                                >
                                  {link.text} <ExternalLinkIcon />
                                </a>
                              ))}
                            </div>
                          )}

                          {project.note && <span className="note">{project.note}</span>}
                        </li>
                      ))}
                    </div>
                  </motion.section>
                );
              })}
            </AnimatePresence>
          </div>

          {filteredData.isFiltering && filteredData.matchCount === 0 && (
            <div className="no-results">No matching credits found.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditsPage;
